import React, { Component } from 'react';

import cloneDeep from 'lodash/cloneDeep';
import isNil from 'lodash/isNil';

import Button from '@atlaskit/button';
import { DatePicker } from '@atlaskit/datetime-picker';
import { Field } from '@atlaskit/form';
import Textfield from '@atlaskit/textfield';

import { ModalBody, ModalFooter, ModalHeader } from '@qality/common-components';
import Editor from '@qality/common-components/src/editor';
import { messages } from '@qality/common-messages';
import { ProjectSelectOption } from '@qality/common-model';
import {
  FlagService,
  TestCycleService,
  VersionService,
  testCycleFormValidationService,
} from '@qality/common-service';
import { Errors } from '@qality/common-service/src/testCycleFormValidationService';

import RecentAndMoreProjectSelect from '../select/RecentAndMoreProjectSelect';
import VersionSelect, { VersionSelectOption } from '../select/VersionSelect';
import './_create-test-cycle-form.scss';

type Props = {
  onSubmit: (testCycleId: number) => void;
  onCancel: () => void;
  isAddingTestCases: boolean;
};

type State = {
  errors: Errors;
  name: string;
  description: string;
  dueDate: string;
  project: ProjectSelectOption;
  version: VersionSelectOption;
  versions: Array<VersionSelectOption>;
  isDescriptionEditorFocused: boolean;
};

class CreateTestCycleForm extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      errors: {},
      name: '',
      description: '',
      version: null,
      dueDate: null,
      project: null,
      versions: [],
      isDescriptionEditorFocused: false,
    };
  }

  handleNameChange = (e: React.FormEvent<HTMLInputElement>): void => {
    let newErrors = cloneDeep(this.state.errors);
    newErrors = testCycleFormValidationService.validateName(
      e.currentTarget.value,
      newErrors
    );

    this.setState({ name: e.currentTarget.value, errors: newErrors });
  };

  handleDescriptionChange = (value: string): void => {
    let newErrors = cloneDeep(this.state.errors);
    newErrors = testCycleFormValidationService.validateDescription(
      value,
      newErrors
    );

    this.setState({ description: value, errors: newErrors });
  };

  handleDueDateChange = (value: string): void => {
    this.setState({
      dueDate: value,
    });
  };

  handleProjectChange = async (value: ProjectSelectOption): Promise<void> => {
    let newErrors = cloneDeep(this.state.errors);
    newErrors = await testCycleFormValidationService.validateProject(
      value,
      newErrors
    );
    this.setState({
      versions: await VersionService.getVersionOptions(value.value),
      errors: newErrors,
    });
  };

  handleVersionChange = (value: VersionSelectOption): void => {
    this.setState({
      version: value,
    });
  };

  validate = async (): Promise<boolean> => {
    const { name, project, description, errors } = this.state;
    let newErrors = cloneDeep(errors);

    newErrors = testCycleFormValidationService.validateName(name, newErrors);
    newErrors = await testCycleFormValidationService.validateProject(
      project,
      newErrors
    );
    newErrors = testCycleFormValidationService.validateDescription(
      description,
      newErrors
    );

    this.setState({ errors: newErrors });
    return Object.keys(newErrors).length === 0;
  };

  // eslint-disable-next-line complexity
  save = async (): Promise<void> => {
    if (!(await this.validate())) {
      return;
    }
    const { name, project, version, description, dueDate } = this.state;

    const result = await TestCycleService.createTestCycle(
      name,
      project.value,
      version ? (version.value !== '-1' ? version.value : null) : null,
      description,
      dueDate
    );

    if (result) {
      FlagService.createSuccessFlag(
        'Success',
        messages['testCycle.creation.success']
      );
      this.props.onSubmit(result.data.id);
    } else {
      FlagService.createErrorFlag(
        'Error',
        messages['testCycle.creation.error']
      );
    }
  };

  handleBlur = (): void => {
    this.setState({
      isDescriptionEditorFocused: false,
    });
  };

  handleClick = (): void => {
    if (!this.state.isDescriptionEditorFocused) {
      this.setState({
        isDescriptionEditorFocused: true,
      });
    }
  };

  // eslint-disable-next-line max-lines-per-function
  render(): JSX.Element {
    const submitButtonMessage = this.props.isAddingTestCases
      ? 'Create and add test cases'
      : 'Create';

    return (
      // TODO: change scss to styled components and remove below div
      <div id='com_soldevelo_apps_test-management_create_test_cycle_form_container'>
        <ModalHeader title='Create test cycle' />
        <ModalBody>
          <div className='create-test-cycle-form'>
            <Field name='name' label='Name'>
              {({ fieldProps }) => (
                <>
                  <Textfield
                    {...fieldProps}
                    onChange={this.handleNameChange}
                    isInvalid={!!this.state.errors.name}
                  />
                  <div className='create-test-cycle-warning-message'>
                    {this.state.errors.name}
                  </div>
                </>
              )}
            </Field>
            <div className='create-test-cycle-form-field create-test-cycle-form-select'>
              <Field name='project' label='Project'>
                {() => (
                  <>
                    <RecentAndMoreProjectSelect
                      onChange={(value: ProjectSelectOption) => {
                        this.setState({ project: value, version: null });
                        this.handleProjectChange(value);
                      }}
                      useAllOption={false}
                      addNameOfFilter={false}
                      isDisabled={false}
                      tooltip={undefined}
                      showLabel={false}
                      isInvalid={!!this.state.errors.project}
                      isInModal={true}
                      //projectId='10025' TODO: provide id
                    />
                    <div className='create-test-cycle-warning-message'>
                      {this.state.errors.project}
                    </div>
                  </>
                )}
              </Field>
            </div>
            <div className='create-test-cycle-form-field create-test-cycle-form-select'>
              <Field name='version' label='Version'>
                {() => (
                  <>
                    <VersionSelect
                      onChange={this.handleVersionChange}
                      selectValue={this.state.version}
                      selectValues={this.state.versions}
                      isDisabled={isNil(this.state.project)}
                      tooltip={
                        messages['testCycle.create.versionSelect.tooltip']
                      }
                    />
                    <div className='create-test-cycle-warning-message'>
                      {this.state.errors.version}
                    </div>
                  </>
                )}
              </Field>
            </div>
            <div className='create-test-cycle-form-field create-test-cycle-form-description'>
              <Field name='description' label='Description'>
                {() => (
                  <>
                    <Editor
                      onClick={this.handleClick}
                      onBlur={this.handleBlur}
                      onChange={this.handleDescriptionChange}
                      isDisabled={false}
                      isFocused={this.state.isDescriptionEditorFocused}
                      listEnabled={true}
                      shouldLoadToolbar={false}
                    />
                    <div className='create-test-cycle-warning-message'>
                      {this.state.errors.description}
                    </div>
                  </>
                )}
              </Field>
            </div>
            <div className='create-test-cycle-form-field test-cycle-dialog-form-due-date hide-focus-wrapper'>
              <Field name='due-date' label='Due date'>
                {() => (
                  <DatePicker
                    id='datepicker-1'
                    placeholder='DD/MM/YYYY'
                    onChange={this.handleDueDateChange}
                    locale='en-GB'
                  />
                )}
              </Field>
              {this.state.errors.dueDate}
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            appearance='subtle'
            onClick={
              () =>
                this.props.onCancel() /* to avoid sending event as param, Jira can't handle it */
            }
          >
            Cancel
          </Button>
          <Button appearance='primary' onClick={this.save}>
            {submitButtonMessage}
          </Button>
        </ModalFooter>
      </div>
    );
  }
}

export default CreateTestCycleForm;
